import { useCallback, useEffect, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { useReactFlow } from "reactflow";
import { ClipboardIcon } from "@heroicons/react/outline";
import { v7 as uuid } from "uuid";
import { FlowStepData } from "@hilos/types/flow";
import { STEP_TYPES_WITHOUT_NEXT } from "../constants/steps";

interface EdgeContextMenuProps {
  edgeId: string;
  top: number;
  left: number;
  copiedStep: FlowStepData | null;
  onClose: () => void;
  onPasteStep: (params: {
    edgeId: string;
    stepId: string;
    step: FlowStepData;
  }) => void;
}

function EdgeContextMenu({
  edgeId,
  top,
  left,
  copiedStep,
  onClose,
  onPasteStep,
}: EdgeContextMenuProps) {
  const [t] = useTranslation();
  const menuRef = useRef<HTMLDivElement>(null);
  const { getEdge, getNode } = useReactFlow();

  const edge = useMemo(() => getEdge(edgeId), [edgeId, getEdge]);

  const allowPasteStep = useMemo(() => {
    if (!copiedStep) return false;
    if (!edge || !edge.source) return false;

    const sourceNode = getNode(edge.source);
    if (!sourceNode) return false;

    if (
      !edge.type?.includes("add_") &&
      edge.target &&
      STEP_TYPES_WITHOUT_NEXT.includes(copiedStep.step_type)
    ) {
      return false;
    }

    return true;
  }, [edge, copiedStep, getNode]);

  const handlePaste = useCallback(() => {
    if (!copiedStep) return;

    const stepId = uuid();
    onPasteStep({
      edgeId,
      stepId,
      step: copiedStep,
    });
    onClose();
  }, [copiedStep, edgeId, onPasteStep, onClose]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose();
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("click", handleClickOutside);

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        onClose();
      }
    }
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("click", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [onClose]);

  if (!edge) return null;

  return createPortal(
    <div
      ref={menuRef}
      className="absolute z-50 bg-white rounded-md shadow-lg border border-gray-200 overflow-hidden"
      style={{ top, left }}
    >
      <div className="py-1">
        <button
          type="button"
          className={`flex w-full items-center px-4 py-2 text-sm ${
            allowPasteStep
              ? "text-gray-700 hover:bg-gray-100 hover:text-gray-900"
              : "text-gray-400 cursor-not-allowed"
          }`}
          onClick={handlePaste}
          disabled={!allowPasteStep}
          title={
            !copiedStep
              ? t("flows:context-menu.no-step-copied", "No step copied")
              : !allowPasteStep
              ? t(
                  "flows:context-menu.cannot-paste-here",
                  "This step type cannot be placed here"
                )
              : ""
          }
        >
          <ClipboardIcon
            className={`mr-3 h-5 w-5 ${
              allowPasteStep ? "text-gray-400" : "text-gray-300"
            }`}
            aria-hidden="true"
          />
          {t("flows:context-menu.paste", "Paste step")}
        </button>
      </div>
    </div>,
    document.body
  );
}

export default EdgeContextMenu;
